import jwtDecode from 'jwt-decode'
import moment from 'moment'
import { getDateTimeFormat } from 'common/dateUtils'
import history from '../../containers/History'
import * as Constants from './../../constants'
import { handleOnPatientClick } from '../../pages/PatientFile/module'
import { updateLoginError } from '../../pages/Login/module'
import { updatePreviousLocation } from '../../store/appReducer'
import { makeSimilarReactRouterLocationObject } from '../../containers/Utilities'
import {
  fetchGetJWT,
  fetchRenewJWT,
  fetchSsoJWT,
  fetchGetSSOJWT,
  XmlHttpRequestSyncDeleteJwt,
} from '../../containers/ApiUrls'

// ------------------------------------
// Constants
// ------------------------------------
const PREFIX = 'AUTH.'
export const SEND_LOGOUT_WARNING = `${PREFIX}SEND_LOGOUT_WARNING`
export const CLEAR_AUTHENTICATION = `${PREFIX}CLEAR_AUTHENTICATION`
export const SET_AUTHENTICATION = `${PREFIX}SET_AUTHENTICATION`
export const AUTHENTICATION_REQUEST = `${PREFIX}AUTHENTICATION_REQUEST`
export const AUTHENTICATION_FAILURE = `${PREFIX}AUTHENTICATION_FAILURE`
export const AUTHENTICATION_SUCCESS = `${PREFIX}AUTHENTICATION_SUCCESS`
export const CHANGE_AUTHORIZATION_TYPE = `${PREFIX}CHANGE_AUTHORIZATION_TYPE`

// ------------------------------------
// Actions
// ------------------------------------
export function clearState() {
  return {
    type: Constants.CLEAR_STATE,
  }
}

export function sendLogoutWarning(bool) {
  return {
    type: SEND_LOGOUT_WARNING,
    payload: bool,
  }
}
export function authenticationRequest() {
  return {
    type: AUTHENTICATION_REQUEST,
  }
}
export function authenticationFailure(err) {
  return {
    type: AUTHENTICATION_FAILURE,
    payload: err,
  }
}
export function authenticationSuccess() {
  return {
    type: AUTHENTICATION_SUCCESS,
  }
}
export function clearAuthentication() {
  clearToken()
  return {
    type: CLEAR_AUTHENTICATION,
  }
}

export function setAuthentication(jwt) {
  setToken(jwt)
  return {
    type: SET_AUTHENTICATION,
  }
}

export const changeAuthorizationType = authType => ({
  type: CHANGE_AUTHORIZATION_TYPE,
  payload: authType,
})

export const setToken = (token = '') => {
  localStorage.setItem(Constants.LOCAL_STORAGE_AUTH_TOKEN_KEY, token)
}

export const clearToken = () => {
  localStorage.removeItem('scripload')
  localStorage.removeItem(Constants.LOCAL_STORAGE_AUTH_TOKEN_KEY)
}

export const getToken = () =>
  localStorage.getItem(Constants.LOCAL_STORAGE_AUTH_TOKEN_KEY) || ''

export const clearResetIdleTime = () => {
  localStorage.removeItem(Constants.LOCAL_STORAGE_IDLE_TIME_KEY)
}
const setResetIdleTime = () => {
  const now = new Date()
  localStorage.setItem(Constants.LOCAL_STORAGE_IDLE_TIME_KEY, now)
}

const getResetIdleTime = () => {
  const idleTime = localStorage.getItem(Constants.LOCAL_STORAGE_IDLE_TIME_KEY)
  if (idleTime !== null) {
    return new Date(idleTime)
  }
  return null
}

const APP_ACTION_BLACKLIST = [
  // exclude third party library actions like @@fedux-form, @@INIT
  /^@@/,
  // AUTH is noisy since it doesn't interval checks. We want app user interactions evecnts
  /^AUTH\./,
  // Following are legacy app actions that is part of the check for action === 'function'
  /^APP\./,
]
function isIdleTimeAction(action) {
  const { type } = action
  if (type) {
    const found = APP_ACTION_BLACKLIST.find(
      regex => type.match(regex) !== null,
    )
    return !found
  }
  return false
}

export const idleTimeUpdater = ({ dispatch, getState }) => next => action => {
  const now = new Date()
  const lastIdleTime = getResetIdleTime() || now
  const timeDelta = Math.floor(now.getTime() - lastIdleTime.getTime()) // time delta in milliseconds
  const idleTimeUpdaterThresholdMs = 1000 * 60 // 1 min
  // Don't keep spamming isactive so just delay for a min
  if (
    (typeof action === 'function' || isIdleTimeAction(action)) &&
    (getResetIdleTime() === null || timeDelta > idleTimeUpdaterThresholdMs)
  ) {
    // console.log(action)
    // console.log('... setting idle time ...')
    setResetIdleTime()
    // dispatch(sendLogoutWarning(false))
  }
  return next(action)
}

export const timeRemainingOnTokenMs = () => {
  const token = getToken()
  const tokenExists = token && token.length > 0

  // return no time left on token if token does not exist
  if (!tokenExists) {
    return 0
  }

  // Try to decode token as if it was valid
  // if valid, return time remaining on token
  // else just return no time left on token
  let decoded
  try {
    decoded = jwtDecode(token)
    const tokenExpiresAt = new Date(decoded.exp * 1000)
    const now = new Date()
    const remainingJwtTime = Math.floor(
      tokenExpiresAt.getTime() - now.getTime(),
    ) // time remaining in milliseconds
    // console.log('timeRemainingOnTokenMs', remainingJwtTime)
    return remainingJwtTime < 0 ? 0 : remainingJwtTime
  } catch (err) {
    // console.log(err)
    return 0
  }
}

export const getJwtWithNewCredentials = credentials => (dispatch, getState) =>
  dispatch(fetchGetJWT(credentials)).then(jwt =>
    dispatch(setAuthentication(jwt)))
export const authLoginSSO = credentials => (dispatch, getState) => {
  dispatch(authenticationRequest())

  // Try to get a JWT token for the first time (using basic auth)
  return (
    dispatch(fetchGetSSOJWT(credentials))
      .then(jwt => dispatch(setAuthentication(jwt)))
      // TODO: find better spot to change this
      // FIX: add stricter authorization by default but need jwt to support sesison type
      // .then(() => dispatch(changeAuthorizationType(Constants.AUTHORIZATION_TYPES.PHYSICIAN)))
      .then(() => dispatch(intervalAuthenticationChecker()))
      .then(() => dispatch(authenticationSuccess()))
      .catch(err => {
        dispatch(clearAuthentication())
        if (err.error && err.status === 403) {
          const { lockoutEndAt } = err.error
          if (lockoutEndAt) {
            const endDate = moment(lockoutEndAt).format(getDateTimeFormat())
            dispatch(
              authenticationFailure(
                Constants.LOGIN_ERROR_MESSAGES.LOCKED_WITH_TIME.replace(
                  '%datetime',
                  endDate,
                ),
              ),
            )
          } else {
            dispatch(
              authenticationFailure(
                `${Constants.LOGIN_ERROR_MESSAGES.LOCKED_OUT}`,
              ),
            )
          }
        } else {
          dispatch(
            authenticationFailure(
              Constants.LOGIN_ERROR_MESSAGES.BAD_CREDENTIALS,
            ),
          )
        }
        // throw away err
      })
  )
}
export const authLogin = credentials => (dispatch, getState) => {
  dispatch(authenticationRequest())

  // Try to get a JWT token for the first time (using basic auth)
  return (
    dispatch(fetchGetJWT(credentials))
      .then(jwt => dispatch(setAuthentication(jwt)))
      // TODO: find better spot to change this
      // FIX: add stricter authorization by default but need jwt to support sesison type
      // .then(() => dispatch(changeAuthorizationType(Constants.AUTHORIZATION_TYPES.PHYSICIAN)))
      .then(() => dispatch(intervalAuthenticationChecker()))
      .then(() => dispatch(authenticationSuccess()))
      .catch(err => {
        dispatch(clearAuthentication())
        if (err.error && err.status === 403) {
          const { lockoutEndAt } = err.error
          if (lockoutEndAt) {
            const endDate = moment(lockoutEndAt).format(getDateTimeFormat())
            dispatch(
              authenticationFailure(
                Constants.LOGIN_ERROR_MESSAGES.LOCKED_WITH_TIME.replace(
                  '%datetime',
                  endDate,
                ),
              ),
            )
          } else {
            dispatch(
              authenticationFailure(
                `${Constants.LOGIN_ERROR_MESSAGES.LOCKED_OUT}`,
              ),
            )
          }
        } else {
          dispatch(
            authenticationFailure(
              Constants.LOGIN_ERROR_MESSAGES.BAD_CREDENTIALS,
            ),
          )
        }
        // throw away err
      })
  )
}

// Used on page change in RouteManager and network requests in ApiUrls
export const renewAuth = () => (dispatch, getState) =>
  dispatch(fetchRenewJWT()).then(jwt => setToken(jwt))

export const checkAuthentication = () => (dispatch, getState) => {
  // Check to see how long is left on token
  const tokenTimeRemainingMs = timeRemainingOnTokenMs()
  // console.log('checkAuthentication', tokenTimeRemainingMs)

  if (tokenTimeRemainingMs === 0) {
    // Token no longer valid, clear auth
    dispatch(clearAuthentication())
  } else {
    // On hard page loads, if token still valid,
    // refresh authenticated flag in redux store
    if (!getState().auth.isAuthenticated) {
      dispatch(setAuthentication(getToken()))
    }

    // console.log(isAuthenticated, authorizationType, tokenTimeRemainingMs)

    // TODO: find better spot to change this
    // FIX: add stricter authorization by default but need jwt to support sesison type
    // dispatch(changeAuthorizationType(Constants.AUTHORIZATION_TYPES.PHYSICIAN))
  }

  return getState().auth.isAuthenticated
}

let timer

export const clearIntervalAuthenticationChecker = () => {
  if (timer !== undefined) {
    timer = window.clearInterval(timer)
  }
}
export const intervalAuthenticationChecker = () => (dispatch, getState) => {
  clearIntervalAuthenticationChecker()
  const { authorizationType } = getState().auth
  const ssoTimerInterval = 1000 * 30 // refresh every 30 seconds
  const timerInterval = 1000 * 15 // check every 15 seconds

  switch (authorizationType) {
  case Constants.AUTHORIZATION_TYPES.EPIC:
    timer = window.setInterval(() => {
      const { isAuthenticated } = getState().auth
      if (isAuthenticated) {
        const timeRemainingMs = timeRemainingOnTokenMs()

        if (timeRemainingMs <= 0) {
          // token expired, force user out, logout user
          clearResetIdleTime()
          history.push('/login')
          clearIntervalAuthenticationChecker()
        } else {
          dispatch(renewAuth())
        }
      }
    }, ssoTimerInterval)
    break
  case Constants.AUTHORIZATION_TYPES.EVERYONE:
  case Constants.AUTHORIZATION_TYPES.PHYSICIAN:
  default:
    timer = window.setInterval(() => {
      const { isAuthenticated } = getState().auth
      if (isAuthenticated) {
        const timeRemainingMs = timeRemainingOnTokenMs()
        const now = new Date()
        const lastIdleTime = getResetIdleTime() || now
        const timeDelta = Math.floor(now.getTime() - lastIdleTime.getTime()) // time delta in milliseconds
        const idleTimeUpdaterThresholdMs = 1000 * 60 // 1 mins

        // handle token
        if (timeRemainingMs <= 0) {
          // token expired, force user out, logout user
          clearResetIdleTime()
          dispatch(
            updateLoginError(Constants.LOGIN_ERROR_MESSAGES.SESSION_TIMEOUT),
          )
          dispatch(
            updatePreviousLocation(
              makeSimilarReactRouterLocationObject(window.location),
            ),
          )
          history.push('/login')
          clearIntervalAuthenticationChecker()
        } else if (
          timeRemainingMs > 0 &&
            timeRemainingMs <= Constants.ACTIVE_USER_UPDATE_TOKEN_THRESHOLD_MS
        ) {
          // start thinking about refreshing token when less than or equal to ACTIVE_USER_UPDATE_TOKEN_THRESHOLD_MS
          if (timeDelta <= idleTimeUpdaterThresholdMs) {
            // the user has been active in the last idleTimeUpdaterThresholdMs time period so refresh the token
            dispatch(renewAuth())
          } else {
            // no activity in awhile since idleTimeUpdaterThresholdMs time period, warn user about logout
            if (timeRemainingMs <= Constants.IDLE_WARNING_THRESHOLD_MS) {
              dispatch(sendLogoutWarning(true))
            } else {
              dispatch(sendLogoutWarning(false))
            }
          }
        } else {
          // no-op do nothing!
          // at this point, the user has a fresh token and does not need to worry about renewing their token
          // idle time management is only a concern when the token is becoming stale but not expired yet
        }
      }

      // console.log('intervalAuthenticationChecker', isAuthenticated, getState().auth)
    }, timerInterval)
    break
  }
}

export const handleSsoLogin = query => (dispatch, getState) => {
  const { ssoRoute, ...ssoData } = query

  const handleBeforeUnloadEvent = e => {
    clearIntervalAuthenticationChecker()
    dispatch(syncDeleteJWT())
  }
  window.addEventListener('beforeunload', handleBeforeUnloadEvent)

  dispatch(getSsoJwt(ssoData)).then(ssoResponse => {
    dispatch(intervalAuthenticationChecker())
    const { patient } = ssoResponse
    switch (ssoRoute.toLowerCase()) {
    case Constants.SSO_ROUTES.PATIENT:
      // history.push(`${ROUTES.PATIENTS_PATIENT_FILE}/${patient.id}`)
      // FIX: Circular dependency is caused when this file includes the below function
      // because that file includes CLEAR_STATE from this file which causes CLEAR_STATE
      // to be undefined at the beginning then later be defined across the whole app
      dispatch(handleOnPatientClick(true, true, { patientId: patient.id }))
      break
    default:
      break
    }
  })
}

const getSsoJwt = payload => (dispatch, getState) => {
  dispatch(authenticationRequest())

  return dispatch(fetchSsoJWT(payload))
    .then(data => {
      const { jwt, ...rest } = data
      dispatch(setAuthentication(jwt))
      dispatch(changeAuthorizationType(Constants.AUTHORIZATION_TYPES.EPIC))
      dispatch(authenticationSuccess())

      return rest
    })
    .catch(err => {
      dispatch(authenticationFailure(err))
    })
}

export const syncDeleteJWT = () => (dispatch, getState) => {
  const token = getToken()
  dispatch(clearAuthentication())

  return XmlHttpRequestSyncDeleteJwt(token)
}

// Role based authorization for page routes
// All available routes and public routes defined in constants file
// TODO: Add role based authorization for features
export const getAuthorizationTypeRoutes = authorizationType => {
  const {
    KARDIA_ADMIN,
    ADMIN,
    PHYSICIAN,
    TECHNICIAN,
    PATIENT,
    EPIC,
    EVERYONE,
  } = Constants.AUTHORIZATION_TYPES

  let privateRoutes = []
  const { ROUTES } = Constants
  switch (authorizationType) {
  case KARDIA_ADMIN:
    privateRoutes = [
      ROUTES.PATIENTS,
      ROUTES.PATIENTS_SEARCH_HOME,
      ROUTES.PATIENTS_DIRECTORY,
      ROUTES.PATIENTS_ADD_NEW_PATIENT,
      ROUTES.PATIENT_ENROLLMENT,
      ROUTES.PATIENTS_PATIENT_FILE,
      // routes patient file recording ekg
      ROUTES.DASHBOARD,
      ROUTES.EKG_REVIEW,
      ROUTES.QT_EKG_REVIEW,
      ROUTES.IDTF_KARDIA_RX,
      ROUTES.EKG_REVIEW_EKG_RECORDING,
      ROUTES.REPORTS,
      ROUTES.TELEKARDIA_ADMIN_DASHBOARD,
      ROUTES.TELEKARDIA_ADMIN_FEEDBACKS,
      ROUTES.TELEKARDIA_ADMIN_CONSULTATIONS,
      ROUTES.TELEKARDIA_ADMIN_CALENDAR,
      // routes single report
      ROUTES.EXPORTS,
      ROUTES.SETTINGS,
      // team member file
      ROUTES.EKG_REVIEW_EKG_ANNOTATION,
    ]
    break
  case ADMIN:
    privateRoutes = [
      ROUTES.PATIENTS,
      ROUTES.PATIENTS_SEARCH_HOME,
      ROUTES.PATIENTS_DIRECTORY,
      ROUTES.PATIENTS_ADD_NEW_PATIENT,
      ROUTES.PATIENT_ENROLLMENT,
      ROUTES.PATIENTS_PATIENT_FILE,
      // routes patient file recording ekg
      ROUTES.DASHBOARD,
      ROUTES.EKG_REVIEW,
      ROUTES.QT_EKG_REVIEW,
      ROUTES.IDTF_KARDIA_RX,
      ROUTES.EKG_REVIEW_EKG_RECORDING,
      ROUTES.REPORTS,
      ROUTES.TELEKARDIA_ADMIN_DASHBOARD,
      ROUTES.TELEKARDIA_ADMIN_FEEDBACKS,
      ROUTES.TELEKARDIA_ADMIN_CONSULTATIONS,
      ROUTES.TELEKARDIA_ADMIN_CALENDAR,
      // routes single report
      ROUTES.EXPORTS,
      ROUTES.SETTINGS,
      // team member file
      ROUTES.EKG_REVIEW_EKG_ANNOTATION,
      // IDTF-CCT
      ROUTES.CCT_PATIENTS_LIST,
      ROUTES.CCT_WORK_LIST,
    ]
    break
  case PHYSICIAN:
    privateRoutes = [
      ROUTES.PATIENTS,
      ROUTES.PATIENTS_SEARCH_HOME,
      ROUTES.PATIENTS_DIRECTORY,
      ROUTES.PATIENTS_ADD_NEW_PATIENT,
      ROUTES.PATIENT_ENROLLMENT,
      ROUTES.PATIENTS_PATIENT_FILE,
      // routes patient file recording ekg
      ROUTES.DASHBOARD,
      ROUTES.EKG_REVIEW,
      ROUTES.QT_EKG_REVIEW,
      ROUTES.IDTF_KARDIA_RX,
      ROUTES.EKG_REVIEW_EKG_RECORDING,
      ROUTES.TELEKARDIA_DASHBOARD,
      ROUTES.TELEKARDIA_APPOINTMENTS,
      ROUTES.TELEKARDIA_ALL_APPOINTMENTS,
      ROUTES.TELEKARDIA_ADMIN_DASHBOARD,
      ROUTES.TELEKARDIA_ADMIN_FEEDBACKS,
      ROUTES.TELEKARDIA_ADMIN_CONSULTATIONS,
      ROUTES.TELEKARDIA_ADMIN_CALENDAR,
      ROUTES.REPORTS,
      // routes single report
      ROUTES.EXPORTS,
      ROUTES.SETTINGS,
      // team member file
      // IDTF Route
      ROUTES.EKG_REVIEW_EKG_ANNOTATION,
      // IDTF-CCT
      ROUTES.CCT_PATIENTS_LIST,
      ROUTES.CCT_WORK_LIST,
    ]
    break
  case TECHNICIAN:
    privateRoutes = [
      ROUTES.PATIENTS,
      ROUTES.PATIENTS_SEARCH_HOME,
      ROUTES.PATIENTS_DIRECTORY,
      ROUTES.PATIENTS_ADD_NEW_PATIENT,
      ROUTES.PATIENT_ENROLLMENT,
      ROUTES.PATIENTS_PATIENT_FILE,
      // routes patient file recording ekg
      ROUTES.DASHBOARD,
      ROUTES.EKG_REVIEW,
      ROUTES.QT_EKG_REVIEW,
      ROUTES.IDTF_KARDIA_RX,
      ROUTES.EKG_REVIEW_EKG_RECORDING,
      ROUTES.REPORTS,
      // routes single report
      ROUTES.EXPORTS,
      ROUTES.SETTINGS,
      // team member file
    ]
    break
  case PATIENT:
  case EPIC:
    privateRoutes = [
      ROUTES.PATIENTS_PATIENT_FILE,
      // routes patient file recording ekg
      // routes report file
    ]
    break
  case EVERYONE:
  default:
    break
  }

  return [ ...Constants.PUBLIC_ROUTES, ...privateRoutes ]
}

export const routeIsAuthorized = (authorizationType, pathname) => (
  dispatch,
  getState,
) => {
  const routes = getAuthorizationTypeRoutes(authorizationType)
  // Valid routes that are not in authorized type routes
  const filteredValidRoutes = Object.keys(Constants.ROUTES)
    .map(key => Constants.ROUTES[key])
    .filter(route => !routes.includes(route))

  // return authorization true if pathname in authorized routes
  // for authorization type or invalid route for 404 page
  const isAuthorized =
    routes.includes(pathname) || !filteredValidRoutes.includes(pathname)

  return isAuthorized
}



// WEBPACK FOOTER //
// ./src/legacy/containers/Auth/Auth.js